import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import usePublicationBookings from '@hooks/usePublicationBookings'
import { generateUrls } from '@hooks/usePublicationBooking'
import type { PublicationBookingFilters } from '@hooks/usePublicationBookings'

import { sanitiseObject } from '@functions/utility'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import defaultRequestOptions from '@sections/Admin/packs/NewsDesk/defaultRequestOptions'
import NewsDeskFilters from '@sections/Admin/packs/NewsDesk/components/NewsDeskFilters'

import PublicationBookingsTable from '../PublicationBookingsTable'

const allowedFilters = ['brand_id', 'preflight_status', 'publication_id', 'publication_edition_id', 'string']

const preflightStatuses = [
  { key: 'approved', title: 'Approved' },
  { key: 'rejected', title: 'Rejected' },
  { key: 'required', title: 'Required' },
]

interface PageContentProps {
  pageFilters: PublicationBookingFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const { preflight_status } = pageFilters

  // Set required as default
  const updatedPageFilers = !preflight_status && preflight_status !== ''
    ? { ...pageFilters, preflight_status: 'required' }
    : pageFilters

  const sanitisedFilters = sanitiseObject(updatedPageFilers, allowedFilters)

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const publicationBookingsPayload = usePublicationBookings({
    filters: {
      ...sanitisedFilters,
      approval_date_start: DateTime.now().minus({ weeks: 2 }).toFormat('yyyy-MM-dd'),
      order_by: 'publication_bookings.approval_date asc',
      preflight_status: pageFilters.preflight_status || 'all',
    },
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.publicationBooking,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationBookings,
    filteredPublicationBookingsCount,
    hasFilteredPublicationBookings,
    loading,
  } = publicationBookingsPayload

  const { preflightingUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Preflighting | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="preflighting"
        nestedNavigation
        tabBarItems={[
          {
            href: preflightingUrl,
            icon: faHome,
            key: 'preflighting',
            title: 'preflighting',
          },
        ]}
        title="Preflighting"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredPublicationBookings && <ListBlankState />}

            {!loading && hasFilteredPublicationBookings && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Preflighting">
                <PublicationBookingsTable publicationBookings={filteredPublicationBookings} />

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredPublicationBookingsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NewsDeskFilters
              allowedFilters={allowedFilters}
              pageDefaultFilters={{ preflight_status: 'required' }}
              pageFilters={pageFilters}
              // @ts-expect-error TS2322
              statuses={preflightStatuses}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
